import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrentUser, useKudosForUser, useUsers } from '../../hooks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { KudosCard } from './KudosCard';
import { SlackEmoji } from '../ui/SlackEmoji';
import { KudosClassification } from '../../types';

interface RecentKudosCardProps {
  limit?: number;
}

interface KudosDisplayItem {
  id: string;
  fromUserName: string;
  fromUserPhoto?: string;
  toUserName: string;
  message: string;
  createdAt: string;
}

export const RecentKudosCard: React.FC<RecentKudosCardProps> = ({ limit = 10 }) => {
  const { user: authUser } = useAuth();
  const { data: currentUser } = useCurrentUser();
  const [page, setPage] = useState(1);

  const userId = currentUser?.id || authUser?.id || '';

  const { data: people = [] } = useUsers(true);
  const { data: paginatedKudos, isLoading } = useKudosForUser(userId, page, limit);
  const totalPages = paginatedKudos?.totalPages ?? 0;

  const peopleById = useMemo(() => {
    const map = new Map<string, { name: string; profilePictureUrl?: string }>();
    for (const p of people) {
      map.set(p.id, { name: p.name, profilePictureUrl: p.profilePictureUrl });
    }
    return map;
  }, [people]);

  const visibleKudos: KudosDisplayItem[] = useMemo(() => {
    const kudos = paginatedKudos?.kudos || [];
    return kudos
      .filter((k) => !k.classification || k.classification === KudosClassification.KUDOS)
      .filter((k) => !k.isHidden)
      .map((k) => {
        const from = peopleById.get(k.fromUserId);
        const to = peopleById.get(k.toUserId);

        return {
          id: k.id,
          fromUserName: from?.name || k.fromUserName,
          fromUserPhoto: from?.profilePictureUrl,
          toUserName: to?.name || k.toUserName,
          message: k.message,
          createdAt: k.createdAt,
        };
      });
  }, [paginatedKudos, peopleById]);

  if (!authUser) {
    return null;
  }

  if (!isLoading && visibleKudos.length === 0 && page === 1) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Heart className="h-5 w-5" />
          Kudos
        </CardTitle>
        <CardDescription className="flex items-center gap-1">
          You're special and the team sees it. <SlackEmoji shortcode="party-blob" size={18} />
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-3">
            {visibleKudos.map((k) => (
              <KudosCard
                key={k.id}
                fromUserName={k.fromUserName}
                fromUserPhoto={k.fromUserPhoto}
                toUserName={k.toUserName}
                message={k.message}
                createdAt={k.createdAt}
                variant="compact"
                users={people}
              />
            ))}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
